import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useTranslation } from 'react-i18next';

export default function ShippingPage() {
  const { t } = useTranslation('home');

  const shippingData = [
    { country: 'USA', express: '7-14 days', expressCost: '$$', economy: '15-25 days', economyCost: '$' },
    { country: 'UK', express: '7-14 days', expressCost: '$$', economy: '15-25 days', economyCost: '$' },
    { country: 'Germany', express: '8-15 days', expressCost: '$$', economy: '15-30 days', economyCost: '$' },
    { country: 'France', express: '8-15 days', expressCost: '$$', economy: '15-30 days', economyCost: '$' },
    { country: 'Denmark', express: '8-15 days', expressCost: '$$', economy: '15-30 days', economyCost: '$' },
    { country: 'Canada', express: '7-14 days', expressCost: '$$', economy: '15-25 days', economyCost: '$' },
    { country: 'Australia', express: '8-16 days', expressCost: '$$', economy: '18-32 days', economyCost: '$' },
    { country: 'Netherlands', express: '8-15 days', expressCost: '$$', economy: '15-30 days', economyCost: '$' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <div className="container-main pt-24 pb-16">
        <div className="max-w-3xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6">
            <a href="/" className="hover:text-primary-600 transition-colors">Home</a>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-600">Shipping Cost Guide</span>
          </nav>

          <span className="inline-block px-2.5 py-1 rounded text-xs font-medium bg-primary-100 text-primary-700 mb-3">Shipping</span>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4">
            AllChinaBuy Shipping Cost Guide 2026
          </h1>
          <div className="flex items-center gap-4 text-sm text-foreground-400 mb-8">
            <span className="flex items-center gap-1"><i className="ri-calendar-line" /> Updated August 25, 2026</span>
            <span className="flex items-center gap-1"><i className="ri-time-line" /> 10 min read</span>
          </div>

          <div className="rounded-lg overflow-hidden mb-8">
            <img
              src="https://readdy.ai/api/search-image?query=International%20shipping%20packages%20and%20delivery%20boxes%20stacked%20neatly%20on%20clean%20conveyor%20belt%2C%20logistics%20warehouse%20concept%2C%20warm%20natural%20lighting%2C%20editorial%20photography%20style%2C%20soft%20beige%20and%20cream%20tones%2C%20professional%20aesthetic%2C%20clean%20minimal%20background%20with%20subtle%20warm%20glow&width=1200&height=600&seq=article-shipping-hero-001&orientation=landscape"
              alt="AllChinaBuy Shipping Cost Guide"
              className="w-full h-64 md:h-80 object-cover"
            />
          </div>

          <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed">
            <p className="text-base mb-6">
              Understanding <strong>AllChinaBuy shipping costs</strong> is essential for budgeting your purchases and choosing the right delivery method. This comprehensive guide breaks down shipping options, costs by country, and money-saving tips for 2026.
            </p>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">Shipping Methods Overview</h2>
            <p className="text-base mb-4">
              AllChinaBuy partners with several international shipping carriers to deliver packages worldwide. The main shipping lines generally fall into two categories: express shipping for faster delivery and economy shipping for cost savings.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm border border-background-200/70 rounded-lg">
                <thead>
                  <tr className="bg-background-100">
                    <th className="px-4 py-3 text-left font-medium text-foreground-700">Method</th>
                    <th className="px-4 py-3 text-left font-medium text-foreground-700">Speed</th>
                    <th className="px-4 py-3 text-left font-medium text-foreground-700">Cost Level</th>
                    <th className="px-4 py-3 text-left font-medium text-foreground-700">Best For</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/50">
                  <tr>
                    <td className="px-4 py-3 font-medium">Express</td>
                    <td className="px-4 py-3">7-16 days</td>
                    <td className="px-4 py-3">Higher ($$)</td>
                    <td className="px-4 py-3">Time-sensitive items, smaller packages</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Economy</td>
                    <td className="px-4 py-3">15-32 days</td>
                    <td className="px-4 py-3">Lower ($)</td>
                    <td className="px-4 py-3">Budget-conscious buyers, non-urgent items</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Sea Mail</td>
                    <td className="px-4 py-3">30-60 days</td>
                    <td className="px-4 py-3">Lowest ($)</td>
                    <td className="px-4 py-3">Very heavy items, no rush</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">Shipping Cost by Country</h2>
            <p className="text-base mb-4">
              The following table provides estimated shipping timelines and relative cost levels for popular destinations. Actual costs depend on package weight, dimensions, and current carrier rates.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm border border-background-200/70 rounded-lg">
                <thead>
                  <tr className="bg-background-100">
                    <th className="px-4 py-3 text-left font-medium text-foreground-700">Country</th>
                    <th className="px-4 py-3 text-left font-medium text-foreground-700">Express (Days)</th>
                    <th className="px-4 py-3 text-left font-medium text-foreground-700">Express Cost</th>
                    <th className="px-4 py-3 text-left font-medium text-foreground-700">Economy (Days)</th>
                    <th className="px-4 py-3 text-left font-medium text-foreground-700">Economy Cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200/50">
                  {shippingData.map((row) => (
                    <tr key={row.country}>
                      <td className="px-4 py-3 font-medium">{row.country}</td>
                      <td className="px-4 py-3">{row.express}</td>
                      <td className="px-4 py-3">{row.expressCost}</td>
                      <td className="px-4 py-3">{row.economy}</td>
                      <td className="px-4 py-3">{row.economyCost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">Factors That Affect Shipping Cost</h2>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5" /> <strong>Package weight:</strong> Heavier items cost more to ship; consider removing shoe boxes to reduce weight</li>
              <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5" /> <strong>Package dimensions:</strong> Volumetric weight can affect cost for large but light items</li>
              <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5" /> <strong>Destination country:</strong> Shipping to remote areas may cost more than major cities</li>
              <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5" /> <strong>Shipping line:</strong> Different carriers have different pricing structures</li>
              <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5" /> <strong>Declared value:</strong> Higher declared values may affect customs fees in some countries</li>
            </ul>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">Tips to Save on Shipping</h2>
            <div className="bg-background-100 rounded-lg p-5 border border-background-200/50 mb-6">
              <ol className="space-y-3">
                <li className="flex items-start gap-3"><span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold flex items-center justify-center flex-shrink-0">1</span> <span><strong>Remove packaging:</strong> Ask to remove shoe boxes and extra packaging to reduce weight</span></li>
                <li className="flex items-start gap-3"><span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold flex items-center justify-center flex-shrink-0">2</span> <span><strong>Consolidate orders:</strong> Combine multiple items into one shipment to spread fixed costs</span></li>
                <li className="flex items-start gap-3"><span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold flex items-center justify-center flex-shrink-0">3</span> <span><strong>Choose economy for non-urgent items:</strong> The savings on economy shipping can be significant</span></li>
                <li className="flex items-start gap-3"><span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold flex items-center justify-center flex-shrink-0">4</span> <span><strong>Rehearse shipping:</strong> Use the shipping calculator in your warehouse to estimate before ordering</span></li>
              </ol>
            </div>

            <div className="bg-accent-50 rounded-lg p-5 border border-accent-200/50 mb-6">
              <p className="text-sm text-accent-800 font-medium mb-2">💡 Important Note</p>
              <p className="text-sm text-accent-700 leading-relaxed">
                Customs fees and import duties are separate from shipping costs and are the buyer's responsibility. These vary by country and item value. Research your local customs thresholds before ordering to avoid unexpected charges.
              </p>
            </div>

            <p className="text-base mb-6">
              For more buying tips, check our <a href="/allchinabuy-spreadsheet-complete-guide" className="text-primary-600 hover:underline">Complete Guide</a> or <a href="/is-allchinabuy-legit" className="text-primary-600 hover:underline">Safety Guide</a>.
            </p>

            <div className="mt-10 bg-primary-50 rounded-lg p-6 md:p-8 text-center border border-primary-200/50">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">Ready to Calculate Your Shipping?</h3>
              <p className="text-sm text-foreground-600 mb-4">Visit our main website to use the shipping calculator and browse products.</p>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="btn-primary inline-flex">
                {t('cta.visit_main')}
                <i className="ri-external-link-line ml-1" />
              </a>
            </div>

            <div className="mt-10 pt-6 border-t border-background-200/50">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">Related Articles</p>
              <div className="flex flex-wrap gap-2">
                <a href="/allchinabuy-spreadsheet-complete-guide" className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">Complete Guide</a>
                <a href="/is-allchinabuy-legit" className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">Safety Guide</a>
                <a href="/allchinabuy-coupon-guide" className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">Coupon Guide</a>
                <a href="/allchinabuy-qc-guide" className="px-3 py-1.5 rounded-md bg-background-100 border border-background-200/50 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors">QC Guide</a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}